import React from "react";

const Section = ({ title, children }) => (
    <section className="space-y-2">
        <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
        <div className="space-y-2 text-slate-700 leading-relaxed">{children}</div>
    </section>
);

export default function Privacy() {
    return (
        <main className="max-w-4xl mx-auto px-6 py-10">
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Informativa sulla privacy</h1>
            <p className="text-sm text-slate-500 mb-8">Ultimo aggiornamento: 10 ottobre 2026</p>

            <div className="space-y-8">
                <Section title="1. Ambito dell’informativa e titolare">
                    <p>
                        Questa informativa descrive il trattamento dei dati personali effettuato tramite
                        Norma® HACCP – Food Safety Pilot, disponibile come servizio web e applicazione mobile
                        Android (il “Servizio”).
                    </p>
                    <p>
                        Per i dati relativi ad account, contratto, assistenza, sicurezza e gestione del Servizio,
                        il titolare del trattamento è <strong>MadOut S.r.l.</strong>, con sede legale in Via della
                        Stazione di Ottavia 57D, 00135 Roma, P. IVA IT15643551003. Per richieste privacy è possibile
                        scrivere a <a className="text-emerald-700 underline" href="mailto:assistenza@norma-haccp.com">assistenza@norma-haccp.com</a>.
                    </p>
                    <p>
                        Quando un cliente inserisce nel Servizio dati personali riferiti a dipendenti, collaboratori,
                        fornitori o altri interessati nell’ambito della propria attività HACCP, il cliente determina
                        finalità e modalità di tale trattamento. In questi casi il cliente opera come titolare e
                        MadOut S.r.l. tratta i dati per suo conto quale responsabile del trattamento.
                    </p>
                </Section>

                <Section title="2. Dati trattati">
                    <p>In funzione delle funzionalità utilizzate, il Servizio può trattare:</p>
                    <ul className="list-disc pl-6 space-y-1">
                        <li>dati di account e contatto, come nome, indirizzo e-mail, numero di telefono, ruolo e credenziali protette;</li>
                        <li>dati dell’attività, come ragione sociale, indirizzo, partita IVA, logo, reparti, orari e dati organizzativi;</li>
                        <li>registri e informazioni operative HACCP, incluse temperature, sanificazioni, ricevimento merci, abbattimenti, non conformità e tracciabilità delle attività;</li>
                        <li>documenti, fotografie e file scelti dall’utente, che possono includere manuali, planimetrie, autorizzazioni e altra documentazione aziendale;</li>
                        <li>dati tecnici e di sicurezza, come indirizzo IP, identificativi di sessione, tipo di dispositivo, versione dell’app, log di accesso, errori e operazioni effettuate;</li>
                        <li>dati provenienti da sensori o dispositivi compatibili, come misurazioni di temperatura e relativi eventi;</li>
                        <li>contenuti delle richieste inviate all’assistenza o alle funzionalità automatizzate, quando utilizzate volontariamente.</li>
                    </ul>
                    <p>
                        L’app può richiedere l’accesso a fotocamera, file, notifiche e dispositivi Bluetooth nelle sole
                        circostanze necessarie a scattare o caricare allegati, salvare o aprire documenti, inviare avvisi
                        e stampare etichette. Le autorizzazioni possono essere revocate dalle impostazioni del dispositivo.
                    </p>
                </Section>

                <Section title="3. Finalità e basi giuridiche">
                    <ul className="list-disc pl-6 space-y-1">
                        <li>erogare il Servizio, autenticare gli utenti, archiviare i registri e fornire le funzioni richieste, in esecuzione del contratto;</li>
                        <li>gestire abbonamento, amministrazione e assistenza, in esecuzione del contratto e per adempiere obblighi di legge;</li>
                        <li>proteggere account, infrastruttura e dati, prevenire abusi e risolvere malfunzionamenti, sulla base del legittimo interesse alla sicurezza del Servizio;</li>
                        <li>inviare notifiche operative richieste o essenziali al funzionamento, in esecuzione del contratto;</li>
                        <li>adempiere obblighi fiscali, contabili, legali o richieste delle autorità competenti;</li>
                        <li>inviare comunicazioni promozionali soltanto quando consentito dalla legge o sulla base del consenso, revocabile in qualsiasi momento.</li>
                    </ul>
                </Section>

                <Section title="4. Modalità, destinatari e trasferimenti">
                    <p>
                        I dati sono trattati con strumenti informatici e misure tecniche e organizzative adeguate.
                        Possono accedervi il personale autorizzato e fornitori necessari all’erogazione del Servizio,
                        ad esempio servizi di hosting, manutenzione, invio di comunicazioni e supporto, vincolati da
                        obblighi di riservatezza e protezione dei dati. I dati non sono venduti.
                    </p>
                    <p>
                        Qualora un fornitore comporti un trasferimento di dati al di fuori dello Spazio Economico
                        Europeo, il trasferimento avviene mediante una decisione di adeguatezza o garanzie riconosciute
                        dal GDPR, incluse, ove applicabili, le clausole contrattuali standard.
                    </p>
                </Section>

                <Section title="5. Conservazione">
                    <p>
                        I dati dell’account e i contenuti operativi sono conservati per la durata del rapporto. Dopo la
                        cessazione, i contenuti del Servizio sono normalmente conservati per un periodo tecnico massimo
                        di 90 giorni e poi cancellati o anonimizzati, salvo obblighi di legge, richieste dell’interessato
                        compatibili con tali obblighi o necessità di tutela in sede giudiziaria. Dati amministrativi e
                        fiscali sono conservati per i termini previsti dalla legge; i log di sicurezza per il tempo
                        proporzionato alla finalità di protezione e diagnosi.
                    </p>
                </Section>

                <Section title="6. Scelte dell’utente e diritti">
                    <p>
                        L’interessato può chiedere accesso, rettifica, cancellazione, limitazione, portabilità e opposizione,
                        nonché revocare il consenso senza pregiudicare i trattamenti già effettuati. Le richieste possono
                        essere inviate a <a className="text-emerald-700 underline" href="mailto:assistenza@norma-haccp.com">assistenza@norma-haccp.com</a>.
                        Quando i dati sono stati inseriti da un’azienda cliente, la richiesta deve essere rivolta in prima
                        istanza a tale azienda, che è titolare del relativo trattamento.
                    </p>
                    <p>
                        È inoltre possibile proporre reclamo al Garante per la protezione dei dati personali tramite
                        il sito <a className="text-emerald-700 underline" href="https://www.garanteprivacy.it" target="_blank" rel="noreferrer">www.garanteprivacy.it</a>.
                    </p>
                </Section>

                <Section title="7. Minori e aggiornamenti">
                    <p>
                        Il Servizio è destinato ad aziende e professionisti e non è rivolto a minori. Questa informativa
                        può essere aggiornata per riflettere modifiche del Servizio o della normativa; la versione vigente
                        è sempre pubblicata in questa pagina con la relativa data di aggiornamento.
                    </p>
                </Section>
            </div>
        </main>
    );
}
